import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BlurEffect = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/blur-effect',
			name: 'Blur Effect on Photos — AI Image Blur Filter | Lift',
			description:
				'Apply a blur effect to photos with AI precision. Select backgrounds, objects, or custom regions with a manual brush. Try Lift.',
			url: 'https://liftapp.ai/blur-effect',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/blur-effect#product' },
				{ '@id': 'https://liftapp.ai/blur-effect#howto' },
				{ '@id': 'https://liftapp.ai/blur-effect#benefits' },
				{ '@id': 'https://liftapp.ai/blur-effect#faq' },
				{ '@id': 'https://liftapp.ai/blur-effect#why-lift' },
				{ '@id': 'https://liftapp.ai/blur-effect#explore-tools' },
				{ '@id': 'https://liftapp.ai/blur-effect#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/blur-effect#product',
			name: 'Blur Effect Online',
			description:
				'Apply a blur effect to photos with AI precision. Select backgrounds, objects, or custom regions with a manual brush.',
			url: 'https://liftapp.ai/blur-effect',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/blur-effect#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your image',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select the target area',
					text: 'Pick a preset selection — background, object, or custom shape — or use the manual brush to target any region with precision',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Adjust blur intensity',
					text: 'Control the strength of the blur effect with a simple slider until the result looks exactly right',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-effect#benefits',
			name: 'Who can benefit from blur effects?',
			description:
				"Lift's blur effect tool is useful for content creators, marketers, small business owners, privacy-conscious users, and casual users who want to apply professional blur effects online.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Direct viewer attention to your subject by applying a blurry photo effect to distracting backgrounds.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Produce clean product visuals with controlled blur effects that eliminate visual noise.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Create polished product imagery by softening busy backgrounds without hiring a photographer.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Privacy-Conscious Users — Conceal sensitive details in screenshots and photos before publishing online.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Casual Users — Apply a blurry photo filter to any image with no editing skills or software required.',
				},
			],
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/blur-effect#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Blur Effect?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift's Blur Effect lets you apply a smooth, adjustable blur effect to any selected region of a photo. Choose a preset — background or object — or use the manual brush for precise control. No design experience needed.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Blur Effect Tool work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your photo, select the region you want to soften — background, a specific object, or a hand-drawn area — then set the blur intensity. AI refines the edges for a natural, seamless result. Download your photo when ready.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Blur Effect Tool uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-effect#why-lift',
			name: "Why Lift's blur effect stands out",
			description:
				"Lift's blur effect combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-effect#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/blur-effect#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Blur Effect Online',
					item: 'https://liftapp.ai/blur-effect',
				},
			],
		},
	],
};
